import React, { useCallback, useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import AuthMFA from './AuthMFA'
import MfaEnrollment from './MfaEnrollment'

const AppWithMFA = ({ children }) => {
  const { signOut } = useAuth()
  const [gateState, setGateState] = useState('checking')
  const [signOutError, setSignOutError] = useState('')

  const checkMFALevel = useCallback(async () => {
    setGateState('checking')

    try {
      console.log('🔐 AppWithMFA: Checking AAL level...')

      // First check if Supabase client is available (API-only mode might not have it)
      if (!supabase?.auth?.mfa) {
        console.log('🔐 AppWithMFA: No Supabase MFA client available - blocking access')
        setGateState('blocked')
        return
      }

      const assuranceResult = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()

      if (!assuranceResult || assuranceResult.error !== null) {
        console.log('🔐 AppWithMFA: AAL check error:', assuranceResult?.error)
        setGateState('blocked')
        return
      }

      const { data } = assuranceResult
      console.log('🔐 AppWithMFA: AAL result:', data)

      const assurancePair = `${data?.currentLevel}:${data?.nextLevel}`
      const validAssurancePairs = ['aal1:aal1', 'aal1:aal2', 'aal2:aal2']
      if (!validAssurancePairs.includes(assurancePair)) {
        setGateState('blocked')
        return
      }

      if (assurancePair === 'aal1:aal1') {
        setGateState('enroll')
        return
      }

      // Check if user needs MFA verification (AAL2) but doesn't have it
      if (data.nextLevel === 'aal2' && data.nextLevel !== data.currentLevel) {
        console.log('🔐 AppWithMFA: MFA verification required - user needs to be upgraded to AAL2')
        setGateState('challenge')
      } else {
        console.log('🔐 AppWithMFA: No MFA required or already verified')
        setGateState('allowed')
      }
    } catch (error) {
      console.error('🔐 AppWithMFA: AAL check failed:', error)
      setGateState('blocked')
    }
  }, [])

  const handleSignOut = useCallback(async () => {
    setSignOutError('')

    try {
      if (typeof signOut !== 'function') {
        throw new Error('Application sign out is unavailable')
      }

      const result = await signOut()
      if (result?.error) {
        throw result.error
      }
    } catch (error) {
      console.error('🔐 AppWithMFA: Sign out failed:', error)
      setSignOutError('Sign out failed. Please try again.')
    }
  }, [signOut])

  useEffect(() => {
    checkMFALevel()
  }, [checkMFALevel])

  if (gateState === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking security requirements...</p>
        </div>
      </div>
    )
  }

  if (gateState === 'enroll') {
    return <MfaEnrollment onSuccess={checkMFALevel} />
  }

  if (gateState === 'challenge') {
    return <AuthMFA onSuccess={checkMFALevel} />
  }

  if (gateState === 'blocked') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow">
          <h1 className="text-xl font-semibold text-gray-900">Unable to verify security</h1>
          <p className="mt-2 text-sm text-gray-600">
            We could not confirm this session's security level. Retry the check or sign out.
          </p>
          {signOutError && (
            <p className="mt-3 text-sm text-red-700" role="alert">{signOutError}</p>
          )}
          <div className="mt-6 flex justify-center gap-3">
            <button
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              onClick={checkMFALevel}
            >
              Retry
            </button>
            <button
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={handleSignOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (gateState === 'allowed') {
    return children
  }

  return null
}

export default AppWithMFA
